import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Button, Box, Alert, Typography, Link, InputAdornment } from '@mui/material';
import { Email } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { ResetPasswordRequestDto } from '../authentication.types';

interface PasswordResetFormProps {
  onSubmit: (data: ResetPasswordRequestDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
  success?: boolean;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({
  onSubmit,
  onCancel,
  isLoading = false,
  error,
  success = false,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ResetPasswordRequestDto>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const handleFormSubmit = async (data: ResetPasswordRequestDto) => {
    try {
      await onSubmit(data);
      reset();
    } catch (error) {
      console.error('Password reset request failed:', error);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Check Your Email
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We&apos;ve sent password reset instructions to your email address.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please check your email and follow the instructions to reset your password.
          </Typography>
          <Button variant="outlined" onClick={onCancel} sx={{ mt: 2 }}>
            Back to Login
          </Button>
        </Box>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} sx={{ width: '100%' }}>
        <Typography variant="h4" component="h1" gutterBottom textAlign="center">
          Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
          Enter your email address and we&apos;ll send you instructions to reset your password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Email"
              type="email"
              error={!!errors.email}
              helperText={errors.email?.message}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            size="large"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid || isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Email'}
          </Button>
        </Box>

        <Box textAlign="center" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Remember your password?{' '}
            <Link
              component="button"
              type="button"
              onClick={onCancel}
              underline="hover"
              sx={{ cursor: 'pointer' }}
            >
              Back to Login
            </Link>
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};
